/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace whitespace */

import {
  Injectable,
  OnDestroy
}                                    from '@angular/core';

import { HttpClient }               from '@angular/common/http';
import { AuthenticationService }     from './authentication.service';
import { ScalableWorkEngineService } from "@/services/scalable.work.engine.service";
import { LineSimService }            from './line.sim.service';
import {SimulationJob, SimulationStatusType} from '@/models/sim/simulation.job';
import { SimpleSimJob }              from '@/models/sim/simple.sim.job';
import { BatchSimJob }               from "@/models/sim/batch.sim.job";
import { Logger }                    from '@/util/logger';
import {
  isset,
  empty,
  fromMySQLDate, errorAsText
} from '@/util/util';

import {
  BehaviorSubject,
  Observable,
  Subscription
}                                    from 'rxjs';
import {first}                       from 'rxjs/operators';
import {RemoteSimJob} from "@/models/sim/remote.sim.job";

@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class JobWatcherService
 *
 * @brief this helper service watches Simulation jobs for updates, and publishs the updates
 * as and when needed.  Anyone who wants to listen to the updates can subscribe the provided
 * observables.
 *
 * To actually get the status of jobs that need updating, we use the LineSimService which
 * can talk to the remote line simulator for us.
 *
 * When jobs are already or reach failed, canceled or completed status...they are retained
 * (to be viewed on demand if needed), but no longer monitored.  We only actively monitor
 * while the job is known to be running in some fashion.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class JobWatcherService implements OnDestroy {

  private jobUpdatesSubject: BehaviorSubject<SimulationJob[]>;
  private jobUpdates:        Observable<SimulationJob[]>;

  private jobDeletedSubject: BehaviorSubject<SimulationJob[]>;
  private jobDeleted:        Observable<SimulationJob[]>;

  /* how many seconds between updates */
  private interval: number                       = 2;

  /** active jobs that need monitoring (subset of all jobs) */
  private monitoring: Map<string, SimulationJob> = new Map<string, SimulationJob>();

  /** the watcher for when the project changes */
  private projectSub: Subscription;

  /* standard constructor */
  constructor(
    private http:    HttpClient,
    private auth:    AuthenticationService,
    public  linesim: LineSimService,
    public  swe:     ScalableWorkEngineService
  ) {

    this.jobUpdatesSubject = new BehaviorSubject<SimulationJob[]>([]);
    this.jobUpdates        = this.jobUpdatesSubject.asObservable();

    this.jobDeletedSubject = new BehaviorSubject<SimulationJob[]>([]);
    this.jobDeleted        = this.jobDeletedSubject.asObservable();

    /* every 1 sec, we try to fetch updates for the jobs that we are watching */
    setTimeout(() => {

      this.update();

    }, this.interval * 1000);
  }

  /** return a unique key we can use to hash simulation jobs with */
  public simKey(engine: string, jobId: number): string {

    return `job-${engine}-${jobId}`;
  }

  /** clear out any job models for the current project */
  public clear(): void {

    /*
     * NOTE: there shouldn't be a reason to call this method; the jobs panel is no longer
     * "show the jobs we've been running in the project manager".  Now it's a general display
     * of all recent jobs from all the simulation nodes (cloudsim, linesim and linesimbeta).
     * So it's not "per project".  Its just global now.  So you wouldn't ever clear it, it
     * has its own means of keeping up to date with all of the simulation nodes.
     *
     */

    Logger.debug(`[JobWatcher] clearing jobs...`);

    this.monitoring.clear();

    /* notify any job watchers that we're clearing the board... */

    this.jobUpdatesSubject.next([]);
  }

  /** add the given jobs, and start monitoring any that need monitoring */
  monitor(jobs: SimulationJob[]): void {

    Logger.debug(`[JobWatcher] adding simulation (${jobs.length}) jobs...`);

    jobs.forEach((job: SimulationJob) => {

      if (job.jobId === 0) {

        /* can skip fake jobs */

        return ;
      }

      if (job.active()) {

        this.monitoring.set(this.simKey(job.config.engine, job.jobId), job);
      }

    });

  }

  /* check for new status details on the jobs we are watching */
  update(): void {

    /* we might be watching a lot of stuff, break the jobs to update into groups of 50 */

    const   batchGroup: number[]                         = [];
    const simpleGroups: string[][]                       = [];
    let    bySimulator: {url: string, group: string[]}[] = [];

    for (const k of this.monitoring.keys()) {

      if ( !this.monitoring.get(k).active()) {

        /* we can stop monitoring this one */

        this.monitoring.delete(k);

        continue;
      }

      /* if it's a batch job being monitored, we have to add that to the batch group */

      if (this.monitoring.get(k) instanceof BatchSimJob) {

        batchGroup.push((this.monitoring.get(k) as SimpleSimJob).jobId);

        continue;
      }

      /* otherwise, prepare to handle the one-offs (there may be a lot if user went click-happy) */

      /*
       * we can't just dump into groups to use for batch progress checking; we have to first
       * group by simulator; since some jobs may be running at different remote simulators.
       *
       */

      const job: SimulationJob = this.monitoring.get(k);
      const endpoint: string   = job.config.endpoint;

      /* do we have a group yet for this simulator? */

      let entry: {url: string, group: string[]} = bySimulator.find(x => x.url == endpoint);

      if (!entry) {

        /* start a new group of jobs, for this simulator */

        bySimulator.push(
          {
            url: endpoint,
            group: [k]
          }
        );

      } else {

        /*
         * append everything for a simulator to a group, but don't let the per simulator group
         * be more than 50 at a time; just so we aren't overwhelming the remote simulator.  So,
         * we may actually have multiple groups per simulator; but every group has only jobs
         * from the same simulator.
         *
         */

        if (entry.group.length >= 50) {

          /* time to flush this group ... */

          simpleGroups.push(entry.group);

          entry.group = [];
        }

        /* append this job to the group... */

        entry.group.push(k);
      }

    } /* end of grouping with capped group sizes */

    /* flush any groups that haven't been flushed yet */

    bySimulator.forEach ( (item: {url: string, group: string[]} ) => {

      simpleGroups.push(item.group);

      item.group = [];

    });

    /*
     * we can't early exit here; we have to fall through to the bottom even if there is nothing
     * being monitored right now...because we have to schedule the recurring update check (at
     * the bottom)
     *
     */

    /* walk the batches and get the status of each batch */

    if (batchGroup.length > 0 ) {

      this.swe.summaries(batchGroup).pipe(first())
        .subscribe({
          next: (summaries: any): void => {

            const updated: BatchSimJob[] = [];

            summaries.forEach((summary: any): void => {

              const jid = summary.result_id;

              if (!this.monitoring.has(jid)) {

                /* expired? */

                return;
              }

              /*
               * expecting:
               *
               *  (
               *      [batch_id] => 45
               *      [result_id] => 3
               *      [google_id] => batch-anthos-cc4442db-9edb-4f13-99f3-b3d02aab1a0f
               *      [status] => failed
               *      [state] => cleaned
               *      [name] => I'm a new result (45)
               *      [error] =>
               *      [summary] => Google has been cleaned.
               *      [totals] => stdClass Object
               *          (
               *              [total] => 6
               *              [running] => 0
               *              [failed] => 6
               *              [completed] => 0
               *              [remaining] => 0
               *          )
               *
               *      [progress] => 100
               *      [created] => 2022-03-04 01:26:49
               *      [updated] => 2022-03-04 01:28:03
               *      [meta] => {"marker": {"cleaned": "2022-03-04 01:28:03", "created": "2022-03-04 01:26:51", "launched": "2022-03-04 01:27:43", "uploaded": "2022-03-04 01:27:32", "data_ready": "2022-03-04 01:28:03", "downloaded": "2022-03-04 01:27:53"}, "trigger": {"cleaned": "2022-03-04 01:27:53", "launched": "2022-03-04 01:27:32", "uploaded": "2022-03-04 01:27:27", "downloaded": "2022-03-04 01:27:52"}, "blob_root": "/users/mgarvin/projects/crash/corridor_batches/35c798d4-c2a8-46f7-ae7a-2995d5365085/adf54a9b-43f3-4edc-a868-1dad639d8875/results/I'm a new result (45)", "state_machine": {"rid": 3, "state": "cleaned", "history": [{"to": "created", "from": "none", "error": "", "comment": "", "timestamp": "2022-03-04 01:26:51"}, {"to": "uploaded", "from": "created", "error": "", "comment": "", "timestamp": "2022-03-04 01:27:32"}, {"to": "launched", "from": "uploaded", "error": "", "comment": "", "timestamp": "2022-03-04 01:27:43"}, {"to": "waiting", "from": "launched", "error": "", "comment": "", "timestamp": "2022-03-04 01:27:43"}, {"to": "data_ready", "from": "waiting", "error": "", "comment": "", "timestamp": "2022-03-04 01:27:52"}, {"to": "downloaded", "from": "data_ready", "error": "", "comment": "", "timestamp": "2022-03-04 01:27:53"}, {"to": "cleaned", "from": "downloaded", "error": "", "comment": "", "timestamp": "2022-03-04 01:28:03"}], "autosave": true}, "subscriptions": {"death": "anthos.batchsim-1.0.0.death.batch_client_3-clientsub", "errors": "anthos.batchsim-1.0.0.errors.batch_client_3-clientsub", "events": "anthos.batchsim-1.0.0.events.batch_client_3-clientsub"}, "worker_configs": {"test": {}, "work": {}, "work_kpi": {}, "work_sim": {"dispatch": "workblox", "loglevel": "error", "max_days": 28, "permissive": false, "passthrough": true, "lockup_hours": 720}, "test_ping": {}, "work_ping": {}, "work_slice": {}}}
               *  )
               *
               */

              const job: BatchSimJob = this.monitoring.get(jid) as BatchSimJob;

              /* take in any updates to the job ... */

              job.googleId           = summary.google_id;
              job.summary            = summary.summary;
              job.status             = SimulationJob.statusType(summary.status);
              job.progress           = parseFloat(summary.progress);
              job.lastError          = summary.error;
              job.updated            = fromMySQLDate(summary.updated);

              job.totals.total       = summary.totals.total;
              job.totals.running     = summary.totals.running;
              job.totals.failed      = summary.totals.failed;
              job.totals.completed   = summary.totals.completed;
              job.totals.remaining   = summary.totals.remaining;

              try {

                job.meta = JSON.parse(summary.meta);

              } catch (err) {

                Logger.error(`[JobWatcher] could not parse meta data for job (${jid}):`, err);
              }

              /* if Google has been cleaned, its definitely done */

              if (summary.state == 'cleaned') {

                job.resolved = true;

                Logger.debug(`[JobWatcher] job (${job.jobId}) has resolved (Google Cleaned): ` + SimulationJob.statusString(job.status));
              }

              /*
               * if we see that the download checkpoint is finished...we don't really have to wait for
               * cleaning to finish, we have the downloads, we can proceed :)
               *
               */

              if (isset(job.meta.marker.downloaded) && job.meta.marker.downloaded) {

                job.resolved = true;

                Logger.debug(`[JobWatcher] job (${job.jobId}) has resolved (Downloaded artifacts): ` + SimulationJob.statusString(job.details.status));
              }

              updated.push(job);

            });

            /* publish ... */

            this.jobUpdatesSubject.next(updated);
          },
          error: (err): void => {

            Logger.error(`[JobWatcher] could not fetch result summaries:`, errorAsText(err));

          }
      });
    }

    /* walk the simple sim (one off) groups and get the status of each group */

    simpleGroups.forEach((g: string[]): void => {

      /*
       * to know which simulator this group of jobs is for, just pick one;
       * the groups are also grouped by simulator.
       *
       */

      let endpoint         = '';
      const sample: SimulationJob = this.monitoring.get(g[0]);
      let ids: number[]           = [];

      if ( this.monitoring.has(g[0])) {

        endpoint = sample.config.endpoint;
      }

      for (let jobTag of g) {

        ids.push( (this.monitoring.get(jobTag) as SimpleSimJob).jobId );
      }

      this.linesim.progress(ids, endpoint)
        .pipe(first())
        .subscribe({
          next: (progress: any[]): string => {

            /*
             * the progress is an object that maps job id (numbers) to the job details (objects),
             * to make it easier to work with, convert to a straight array of the values.
             *
             */

            let jobs: RemoteSimJob[] = [];

            for (const [k, v] of Object.entries(progress)) {

              let item: RemoteSimJob = new RemoteSimJob(v);

              jobs.push(item);
            }

            /*
             * we got back details for a job...update the local model for each of them, and then publish
             * this group of updates.
             *
             */

            const updated: SimulationJob[] = [];

            /* update... */

            for (let item of jobs) {

              if ( !this.monitoring.has(this.simKey(sample.config.engine, item.id)) ) {

                /* not being monitored */

                return ;
              }

              const job: SimulationJob = this.monitoring.get(this.simKey(sample.config.engine, item.id));

              if (job instanceof SimpleSimJob) {

                const ssj: SimpleSimJob = job as SimpleSimJob;

                ssj.details = item;

                if (isset(ssj.details.created_at)) {

                  ssj.created = ssj.details.created_at.toJSDate();
                }

                if (isset(ssj.details.dequeue_at)) {

                  ssj.dequeue = ssj.details.dequeue_at.toJSDate();
                }

                if (isset(ssj.details.updated_at)) {

                  ssj.updated = ssj.details.updated_at.toJSDate();
                }

                if (isset(ssj.details.input)) {

                  ssj.inputDownload = ssj.details.input;
                }

                if (isset(ssj.details.output)) {

                  ssj.outputDownload = ssj.details.output;
                }

                if (isset(ssj.details.log)) {

                  ssj.logDownload = ssj.details.log;
                }

                if (isset(ssj.details.extra)) {

                  ssj.extra = ssj.details.extra;

                  /* before publishing the update, do any refined updates, check for a job crash etc. */

                  if (isset(ssj?.extra?.checkpoints?.download_complete)) {

                    /*
                     * the job has completed downloading artifacts from Google to the cloudsim proxy
                     * service, so at this point we are capable of confirming the status as either
                     * failed or complete, and we cna go ahead and download artifacts.  Any that are
                     * going to be available...are available.
                     *
                     * the cloudsim service will still go on to cleaup Google buckets and pub/sub...
                     * but its just housekeeping.  once the downloads are ready...we go.
                     *
                     */

                    ssj.resolved = true;

                    Logger.debug(`[JobWatcher] job (${job.jobId}) has resolved: ` + SimulationJob.statusString(ssj.details.status));

                    /* we may have been able to detect a stack trace for cases where it crashed... */

                    if (isset(ssj.extra.crash)) {

                      ssj.crash = ssj.extra.crash;

                      Logger.debug(`[JobWatcher] CRASH DETECTED, job (${job.jobId}): `, ssj.crash);
                    }

                    /*
                     * make sure we have all our remote downloads setup, cmd, errors and kpi download
                     * URLs are based on the output URL -- which is always set, even if the simulator
                     * crashes (it's a pre-calculated property).
                     *
                     */

                    const pathBits: string[] = ssj.outputDownload.match(/^(.*)\/([^\/]+)\.([^.]+)$/);
                    const dir:      string   = pathBits[1];
                    const fname:    string   = pathBits[2];
                    const ext:      string   = pathBits[3];

                    if (ssj.extra.hasOwnProperty('artifact-sim-cmd-download')) {

                      ssj.cmdDownload = dir + '/' + fname + '-cmd.' + ext;
                    }

                    if (ssj.extra.hasOwnProperty('artifact-sim-errors-download')) {

                      ssj.errorsDownload = dir + '/' + fname + '-errors.txt';
                    }

                    if (ssj.extra.hasOwnProperty('artifact-kpi-download')) {

                      ssj.kpiDownload = dir + '/' + fname + '-kpi.' + ext;
                    }

                    if (ssj.extra.hasOwnProperty('artifact-kpix-download')) {

                      ssj.kpixDownload = dir + '/' + fname + '-kpix.' + ext;
                    }

                  }

                }

                if (isset(ssj.details.log)) {

                  ssj.logDownload = ssj.details.log;
                }

                if (isset(ssj.details.simcmd)) {

                  ssj.cmdDownload = ssj.details.simcmd;
                }

                if (isset(ssj.details.kpi)) {

                  ssj.kpiDownload = ssj.details.kpi;
                }

                if (isset(ssj.details.kpix)) {

                  ssj.kpixDownload = ssj.details.kpix;
                }

                if (isset(ssj.details.simerrors)) {

                  ssj.errorsDownload = ssj.details.simerrors;
                }

                if (isset(ssj.details.lasterror)) {

                  ssj.lastError = ssj.details.lasterror;
                }

                if (isset(ssj.details.progress)) {

                  ssj.progress = parseFloat(ssj.details.progress);
                }

                if (isset(ssj.details.options)) {

                  ssj.options = ssj.details.options;

                }

                if (!isset(ssj.details.response) ||  empty(ssj.details.response)) {

                  if (isset(ssj.details.progress_out)) {

                    ssj.response = ssj.details.progress_out;
                  }

                } else {

                  ssj.response = ssj.details.response;
                }

                Logger.debug(`[JobWatcher] progress (${job.jobId}) ${ssj.progress}`, ssj.response[ssj.response.length - 1]);

                if (isset(ssj.details.status)) {

                  ssj.status = ssj.details.status;

                  /*
                   * for single shot on demand simulations, we don't have the scalable work
                   * engine, automatically moving things along (like automatically starting the
                   * download etc.).  So, we have to watch the over all status, if it flips to
                   * failed to completed...then its time to mark the job as resolved.
                   *
                   */

                  if ((ssj.details.status == SimulationStatusType.FAIL)||(ssj.details.status == SimulationStatusType.SUCCESS)) {

                    ssj.resolved = true;
                  }

                }

              }

              /* queue for update publishing ... */

              updated.push(job);

            }

            /* publish ... */

            this.jobUpdatesSubject.next(updated);

          },
          error: (err): void => {

            Logger.error(`[JobWatcher] could not fetch progress of jobs:`, errorAsText(err));

          }

        });

    });

    /* every 1 sec, we try to fetch updates for the jobs that we are watching */
    setTimeout((): void => {

      this.update();

    }, this.interval * 1000);
  }

  unMonitor(job: SimulationJob) {

    if (job.jobId === 0) {

      /* can skip fake jobs */

      return ;
    }

    this.monitoring.delete(this.simKey(job.config.engine, job.jobId));

    this.jobDeletedSubject.next([job]);
  }

  /** watch for progress/status changes */
  public updates(): Observable<SimulationJob[]> {

    return this.jobUpdates;
  }

  /** watch for results being deleted */
  public deletes(): Observable<SimulationJob[]> {

    return this.jobDeleted;
  }

  /* clean up */
  ngOnDestroy(): void {

    this.projectSub.unsubscribe();
  }


}
